import {
  Bell,
  Building2,
  Check,
  ChevronDown,
  Headset,
  Info,
  LogOut,
  Menu,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAssistenteUi } from '@/features/assistente/assistenteUi';
import { useAuth } from '@/features/auth/AuthProvider';
import { getEscolaAtiva } from '@/features/auth/authGates';
import { authService } from '@/features/auth/authService';
import { NotificacoesPopover } from '@/features/notificacoes/NotificacoesPopover';
import { SobreModal } from '@/features/sobre/SobreModal';
import { useNotificacoesResumo } from '@/features/notificacoes/useNotificacaoQueries';
import { getErrorMessage } from '@/shared/lib/api/errors';
import { useTheme } from '@/shared/hooks/useTheme';
import { useToast } from '@/shared/hooks/useToast';
import { BrandLogo } from '@/shared/ui/BrandLogo/BrandLogo';
import { SafeImage } from '@/shared/ui/SafeImage/SafeImage';
import styles from './Header.module.css';

type HeaderProps = {
  onOpenMegaMenu: () => void;
  onCloseMegaMenu?: () => void;
  megaMenuAberto?: boolean;
  sidebarRetraida?: boolean;
};

export function Header({
  onOpenMegaMenu,
  onCloseMegaMenu,
  megaMenuAberto = false,
  sidebarRetraida = false,
}: HeaderProps) {
  const { user, logout } = useAuth();
  const { abrir: abrirAjuda } = useAssistenteUi();
  const { theme, toggleTheme } = useTheme();
  const toast = useToast();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [escolaMenuOpen, setEscolaMenuOpen] = useState(false);
  const [trocandoEscola, setTrocandoEscola] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [sobreOpen, setSobreOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const escolaRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const resumo = useNotificacoesResumo(!notifOpen);
  const naoLidas = resumo.data?.naoLidas ?? 0;

  const escolaAtiva = getEscolaAtiva(user);
  const multiEscola = (user?.escolas.length ?? 0) > 1;
  const escolas = user?.escolas ?? [];

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (escolaRef.current && !escolaRef.current.contains(e.target as Node)) {
        setEscolaMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleTrocarEscola = async (idEscola: number) => {
    if (trocandoEscola) return;
    if (idEscola === escolaAtiva?.id) {
      setEscolaMenuOpen(false);
      return;
    }
    setTrocandoEscola(true);
    try {
      await authService.selecionarEscola({ idEscola });
      // Reload completo: permissoes (/me.acoes), atalhos e caches de listagem mudam com a escola.
      window.location.assign('/');
    } catch (err) {
      setTrocandoEscola(false);
      toast.error(getErrorMessage(err));
    }
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    void logout();
  };

  return (
    <header
      className={styles.header}
      onClick={() => {
        if (megaMenuAberto) onCloseMegaMenu?.();
      }}
    >
      <div className={styles.left}>
        <div className={`${styles.brandZone} ${sidebarRetraida ? styles.brandZoneRetraida : ''}`}>
          <Link to="/" className={styles.logoLink} aria-label="Ir para o Início">
            {sidebarRetraida ? (
              <BrandLogo variant="light" mark height={28} className={styles.logoMark} />
            ) : (
              <BrandLogo variant="light" height={32} className={styles.logo} />
            )}
          </Link>
        </div>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={(event) => {
            event.stopPropagation();
            if (megaMenuAberto) onCloseMegaMenu?.();
            else onOpenMegaMenu();
          }}
          aria-expanded={megaMenuAberto}
          aria-label={megaMenuAberto ? 'Fechar menu de módulos' : 'Abrir menu de módulos'}
          data-alvo="menu-modulos"
          data-alvo-rotulo="Menu de módulos"
        >
          <Menu size={20} aria-hidden />
        </button>

        <div className={styles.popoverWrap} ref={escolaRef}>
          {multiEscola ? (
            <button
              type="button"
              className={styles.escolaBtn}
              onClick={() => {
                setUserMenuOpen(false);
                setNotifOpen(false);
                setEscolaMenuOpen((v) => !v);
              }}
              aria-expanded={escolaMenuOpen}
              aria-haspopup="menu"
              aria-label="Trocar escola"
              disabled={trocandoEscola}
              data-alvo="trocar-escola"
              data-alvo-rotulo="Trocar escola"
            >
              <strong className={styles.escolaNome}>{escolaAtiva?.nome ?? '—'}</strong>
              <ChevronDown size={16} aria-hidden />
            </button>
          ) : (
            <strong className={styles.escolaNome}>{escolaAtiva?.nome ?? '—'}</strong>
          )}
          {escolaMenuOpen && multiEscola ? (
            <div className={styles.escolaMenu} role="menu" aria-label="Escolas">
              <p className={styles.escolaMenuTitle}>Trocar escola</p>
              {escolas.map((escola) => {
                const ativa = escola.id === escolaAtiva?.id;
                return (
                  <button
                    key={escola.id}
                    type="button"
                    className={`${styles.escolaMenuItem} ${ativa ? styles.escolaMenuItemAtiva : ''}`}
                    role="menuitem"
                    disabled={trocandoEscola}
                    onClick={() => void handleTrocarEscola(escola.id)}
                  >
                    {escola.logoUrl ? (
                      <SafeImage
                        src={escola.logoUrl}
                        alt=""
                        className={styles.escolaMenuLogo}
                        variant="logo"
                      />
                    ) : (
                      <span className={styles.escolaMenuLogoFallback} aria-hidden>
                        <Building2 size={18} />
                      </span>
                    )}
                    <span className={styles.escolaMenuTexto}>
                      <span className={styles.escolaMenuNome}>{escola.nome}</span>
                      {escola.principal ? (
                        <span className={styles.escolaPrincipalBadge}>Principal</span>
                      ) : null}
                    </span>
                    {ativa ? <Check size={18} className={styles.escolaMenuCheck} aria-hidden /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.popoverWrap} ref={notifRef}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => {
              setEscolaMenuOpen(false);
              setUserMenuOpen(false);
              setNotifOpen((v) => !v);
            }}
            aria-label={
              naoLidas > 0 ? `Notificações, ${naoLidas} não lidas` : 'Notificações'
            }
            aria-expanded={notifOpen}
            data-alvo="notificacoes"
            data-alvo-rotulo="Notificações"
          >
            <Bell size={20} />
            {naoLidas > 0 ? (
              <span className={styles.badge}>{naoLidas > 99 ? '99+' : naoLidas}</span>
            ) : null}
          </button>
          {notifOpen ? (
            <NotificacoesPopover
              aberto={notifOpen}
              temNaoLidas={naoLidas > 0}
              onFechar={() => setNotifOpen(false)}
            />
          ) : null}
        </div>

        <div className={styles.popoverWrap} ref={userRef}>
          <button
            type="button"
            className={styles.userBtn}
            onClick={() => {
              setEscolaMenuOpen(false);
              setUserMenuOpen((v) => !v);
            }}
            aria-expanded={userMenuOpen}
            data-alvo="menu-usuario"
            data-alvo-rotulo={
              multiEscola
                ? 'Menu do usuário (Sobre, Ajuda, Sair; no celular, Trocar escola)'
                : 'Menu do usuário (Sobre, Ajuda, Sair)'
            }
          >
            <span className={styles.avatar} aria-hidden>
              {user?.fotoUrl ? (
                <SafeImage src={user.fotoUrl} alt="" variant="avatar" />
              ) : (
                <User size={16} />
              )}
            </span>
            <span className={styles.userName}>{user?.nome ?? 'Usuário'}</span>
            <ChevronDown size={16} />
          </button>
          {userMenuOpen ? (
            <div className={styles.menu} role="menu">
              <div className={styles.menuHeader}>
                <strong>{user?.nome}</strong>
                <span className={styles.menuLogin}>{user?.login}</span>
                {user?.cargoNome ? <span className={styles.menuCargo}>{user.cargoNome}</span> : null}
              </div>
              {multiEscola ? (
                <button
                  type="button"
                  className={`${styles.menuItem} ${styles.somenteResponsivo}`}
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setEscolaMenuOpen(true);
                  }}
                >
                  <Building2 size={16} />
                  Trocar escola
                </button>
              ) : null}
              <button
                type="button"
                className={styles.menuItem}
                role="menuitem"
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                Aparência
              </button>
              <button
                type="button"
                className={styles.menuItem}
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false);
                  abrirAjuda();
                }}
              >
                <Headset size={16} />
                Mia Ajuda
              </button>
              <button
                type="button"
                className={styles.menuItem}
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false);
                  setSobreOpen(true);
                }}
              >
                <Info size={16} />
                Sobre
              </button>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={handleLogout}>
                <LogOut size={16} />
                Sair
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <SobreModal open={sobreOpen} onClose={() => setSobreOpen(false)} />
    </header>
  );
}
